import { ref, computed } from 'vue';
import { enTranslations } from '../locales/en';
import { ruTranslations } from '../locales/ru';
import { ukTranslations } from '../locales/uk';

export type Language = 'en' | 'ru' | 'uk' | 'pl' | 'de' | 'es';

const STORAGE_KEY = 'cafe-language';
const SOURCE_STORAGE_KEY = 'cafe-language-source';
const DEFAULT_LANGUAGE: Language = 'en';
const SUPPORTED: Language[] = ['en', 'ru', 'uk', 'pl', 'de', 'es'];

export const AVAILABLE_LANGUAGES: { code: Language; label: string }[] = [
  { code: 'en', label: 'EN' },
  { code: 'ru', label: 'RU' },
  { code: 'uk', label: 'UK' },
  { code: 'de', label: 'DE' },
  { code: 'pl', label: 'PL' },
  { code: 'es', label: 'ES' },
];

const loadedTranslations = ref<Record<Language, Record<string, string>>>({
  en: enTranslations,
  ru: ruTranslations,
  uk: ukTranslations,
  pl: enTranslations, // fallback until loaded
  de: enTranslations,
  es: enTranslations,
});

const dynamicLoaders: Partial<Record<Language, () => Promise<Record<string, string>>>> = {
  pl: () => import('../locales/pl').then(m => m.plTranslations),
  de: () => import('../locales/de').then(m => m.deTranslations),
  es: () => import('../locales/es').then(m => m.esTranslations),
};

async function ensureLocale(lang: Language) {
  const loader = dynamicLoaders[lang];
  if (!loader || loadedTranslations.value[lang] !== enTranslations) return;
  const dict = await loader();
  loadedTranslations.value = { ...loadedTranslations.value, [lang]: dict };
}

const normalizeLanguage = (value?: string | null): Language | null => {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw) return null;

  for (const lang of SUPPORTED) {
    if (raw === lang || raw.startsWith(`${lang}-`)) return lang;
  }

  if (raw.startsWith('zh')) return null;
  if (raw.startsWith('be')) return 'ru';
  if (raw.startsWith('uk')) return 'uk';
  if (raw.startsWith('sr') || raw.startsWith('bg') || raw.startsWith('mk')) return 'ru';

  return null;
};

const getBrowserLanguage = (): Language => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return DEFAULT_LANGUAGE;
  }

  const candidates = Array.isArray(navigator.languages) && navigator.languages.length
    ? navigator.languages
    : [navigator.language];

  for (const candidate of candidates) {
    const matched = normalizeLanguage(candidate);
    if (matched) return matched;
  }

  return DEFAULT_LANGUAGE;
};

const getInitialLanguage = (): Language => {
  if (typeof window !== 'undefined') {
    const stored = normalizeLanguage(localStorage.getItem(STORAGE_KEY));
    const source = String(localStorage.getItem(SOURCE_STORAGE_KEY) || '').trim().toLowerCase();
    if (stored && source === 'manual') return stored;
    return getBrowserLanguage();
  }
  return DEFAULT_LANGUAGE;
};

const currentLanguage = ref<Language>(getInitialLanguage());

// Pre-fetch the initial language if it needs dynamic loading
ensureLocale(currentLanguage.value);

// Switches the active language without marking it as a manual choice, so a later
// manual pick (source = 'manual') still wins and we don't persist an auto value.
const setAutoLanguage = (lang: Language) => {
  currentLanguage.value = lang;
  if (typeof document !== 'undefined') document.documentElement.lang = lang;
  ensureLocale(lang);
};

// Regional default: ru/uk are shown by default ONLY inside Ukraine. The browser
// language still auto-selects de/es/pl/en as before; this only gates ru/uk by
// geo-IP so visitors elsewhere get English unless they pick ru/uk manually.
export const applyRegionalLanguageDefault = async (): Promise<void> => {
  if (typeof window === 'undefined') return;
  const source = String(localStorage.getItem(SOURCE_STORAGE_KEY) || '').trim().toLowerCase();
  if (source === 'manual') return; // respect an explicit user choice
  if (currentLanguage.value !== 'ru' && currentLanguage.value !== 'uk') return;
  try {
    const response = await fetch('https://ipapi.co/json/');
    const data = await response.json();
    if (data && data.country_code && data.country_code !== 'UA') {
      setAutoLanguage('en');
    }
  } catch {
    // Geolocation unavailable — keep the auto-detected language as-is.
  }
};

export const useLanguage = () => {
  const setLanguage = (lang: Language) => {
    currentLanguage.value = lang;
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, lang);
      localStorage.setItem(SOURCE_STORAGE_KEY, 'manual');
      document.documentElement.lang = lang;
    }
    ensureLocale(lang);
  };

  const t = (key: string, fallback?: string): string => {
    const dict = loadedTranslations.value[currentLanguage.value];
    const enDict = loadedTranslations.value.en;
    return dict[key] || enDict[key] || fallback || key;
  };

  const language = computed(() => currentLanguage.value);

  return { language, setLanguage, t };
};

if (typeof document !== 'undefined') {
  document.documentElement.lang = currentLanguage.value;
}
